import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    if (!token) { toast.error("Missing reset token — request a new link"); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      toast.success("Password reset. You can sign in now.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background bg-grid p-4">
      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size="xl" className="mb-4" />
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a new password for your account.
          </p>
        </div>
        <Card className="border-border bg-card p-6">
          {!token ? (
            <div className="space-y-3 text-center">
              <p className="text-sm text-muted-foreground">This reset link is missing its token.</p>
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">Request a new one</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retype your new password"
                  minLength={8}
                  required
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="reset-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><ShieldCheck className="mr-2 h-4 w-4" /> Set new password</>
                )}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
